import { useMemo } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Activity,
  AlertOctagon,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarRange,
  Download,
  Info,
  Lightbulb,
  Sparkles,
  Target,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  Anomaly,
  DiscoverySection,
  MonthlyReportView,
  RankMovementSection,
  Recommendation,
  Report,
  TechnicalHealthSection,
} from '@seo/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/spinner';
import { StatusPill } from '@/components/status-pill';
import { EmptyState } from '@/components/empty-state';
import { DetailError } from '@/components/detail-page';
import { PageHeader } from '@/components/page-header';
import { GlassSurface } from '@/components/glow-card';
import { KpiTile, type KpiAccent } from '@/components/kpi-tile';
import { useToast, toastError } from '@/components/ui/toast';
import { useReport } from '@/features/reports/use-report';
import {
  monthFromPeriodStart,
  useMonthlyReportView,
} from '@/features/reports/use-monthly-report';
import { useProject } from '@/features/projects/use-project';
import { downloadAuthed } from '@/lib/download';
import { useAuthStore } from '@/lib/auth';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

export function ReportDetailRoute(): JSX.Element {
  const { reportId } = useParams({ strict: false }) as { reportId: string };
  const report = useReport(reportId);
  // Primes the breadcrumb's project crumb AND names the header below.
  const project = useProject(report.data?.project_id);
  const monthly = useMonthlyReportView(
    report.data?.project_id,
    report.data ? monthFromPeriodStart(report.data.period_start) : undefined,
  );
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);
  const { toast } = useToast();

  if (report.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={report.error}
          entityKind="report"
          backTo="/reports"
          backLabel="Reports"
        />
      </div>
    );
  }
  if (report.isLoading || !report.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const r = report.data;

  const downloadPdf = async (): Promise<void> => {
    try {
      await downloadAuthed({
        url: `/v1/reports/${r.id}/report.pdf`,
        filename: `report-${r.id.slice(0, 8)}.pdf`,
        accessToken,
        tenantId: tenant?.id,
      });
    } catch (e) {
      toastError(toast, e);
    }
  };

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      <PageHeader
        title={`${r.cadence.charAt(0).toUpperCase()}${r.cadence.slice(1)} report`}
        eyebrow={project.data?.name}
        description={`Covering ${r.period_start} through ${r.period_end}${
          r.delivered_at
            ? ` · delivered ${new Date(r.delivered_at).toLocaleString()}`
            : ''
        }`}
        actions={
          <>
            <StatusPill status={r.status} />
            <span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:inline-flex">
              <CalendarRange className="h-3.5 w-3.5" />
              {r.period_start} → {r.period_end}
            </span>
            <Button onClick={downloadPdf}>
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </>
        }
      />

      <KpiRow report={r} />

      <AnomaliesSection anomalies={r.anomalies} />

      <RecommendationsSection recommendations={r.recommendations} />

      {monthly.data ? <FreeTierSectionsBlock view={monthly.data} /> : null}

      {r.failure_reason ? (
        <motion.div
          variants={fadeUp}
          className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4 text-sm text-rose-200"
        >
          <div className="mb-1 flex items-center gap-2 font-semibold">
            <AlertOctagon className="h-4 w-4" />
            What went wrong
          </div>
          {r.failure_reason}
        </motion.div>
      ) : null}
    </motion.div>
  );
}

function KpiRow({ report }: { report: Report }): JSX.Element {
  const counts = useMemo(() => {
    const a = report.anomalies ?? [];
    const r = report.recommendations ?? [];
    const critical = a.filter((x) => x.severity === 'critical').length;
    const high = a.filter((x) => x.severity === 'high').length;
    const avgImpact =
      r.length > 0
        ? r.reduce((acc, x) => acc + x.expected_impact, 0) / r.length
        : null;
    const topRec = r.reduce<Recommendation | null>(
      (acc, x) => (acc === null || x.score > acc.score ? x : acc),
      null,
    );
    return { anomalyCount: a.length, critical, high, recCount: r.length, avgImpact, topRec };
  }, [report.anomalies, report.recommendations]);

  const tiles: Array<{
    label: string;
    value: string;
    icon: typeof Activity;
    accent: KpiAccent;
    footnote?: string;
  }> = [
    {
      label: 'Unusual changes',
      value: String(counts.anomalyCount),
      icon: AlertOctagon,
      accent: 'rose',
      footnote:
        counts.critical > 0
          ? `${counts.critical} critical`
          : counts.high > 0
            ? `${counts.high} high`
            : undefined,
    },
    {
      label: 'Recommendations',
      value: String(counts.recCount),
      icon: Lightbulb,
      accent: 'primary',
    },
    {
      label: 'Average impact',
      value: counts.avgImpact !== null ? counts.avgImpact.toFixed(1) : '—',
      icon: Target,
      accent: 'emerald',
    },
    {
      label: 'Best next move',
      value: counts.topRec ? counts.topRec.score.toFixed(1) : '—',
      icon: Sparkles,
      accent: 'violet',
      footnote: counts.topRec?.title,
    },
  ];

  return (
    <motion.section
      variants={fadeUp}
      className="grid grid-cols-2 gap-3 lg:grid-cols-4"
    >
      {tiles.map((t) => (
        <GlassSurface key={t.label} className="p-4">
          <KpiTile label={t.label} value={t.value} icon={t.icon} accent={t.accent} />
          {t.footnote ? (
            <div className="mt-2 truncate text-xs text-muted-foreground">{t.footnote}</div>
          ) : null}
        </GlassSurface>
      ))}
    </motion.section>
  );
}

function AnomaliesSection({
  anomalies,
}: {
  anomalies: Anomaly[] | null;
}): JSX.Element {
  const chartData = useMemo(() => {
    if (!anomalies) return [];
    return anomalies.map((a) => ({
      name: `${a.metric}\n${a.date}`,
      observed: a.observed,
      expected: a.expected,
    }));
  }, [anomalies]);

  return (
    <motion.section variants={fadeUp} className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Unusual changes</h2>
        <p className="text-sm text-muted-foreground">
          Numbers that went further up or down than we'd normally expect this period.
        </p>
      </div>
      {!anomalies || anomalies.length === 0 ? (
        <EmptyState
          title="Everything's steady"
          description="Nothing unusual happened this period — your numbers tracked as expected."
        />
      ) : (
        <div className="space-y-3">
          <GlassSurface className="p-4">
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke="hsl(var(--border) / 0.3)" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    interval={0}
                    angle={-10}
                    height={48}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: 'hsl(var(--accent) / 0.4)' }}
                    contentStyle={{
                      background: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="expected" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="observed" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </GlassSurface>
          <motion.ul
            variants={stagger}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
          >
            {anomalies.map((a, i) => (
              <AnomalyCard key={`${a.metric}-${a.date}-${i}`} anomaly={a} />
            ))}
          </motion.ul>
        </div>
      )}
    </motion.section>
  );
}

function AnomalyCard({ anomaly }: { anomaly: Anomaly }): JSX.Element {
  const meta =
    anomaly.severity === 'critical'
      ? {
          stripe: 'from-rose-500/80 via-rose-500/40 to-transparent',
          halo: 'bg-rose-500/15',
          icon: AlertOctagon,
          iconClass: 'text-rose-300',
          badge: 'destructive' as const,
        }
      : anomaly.severity === 'high'
        ? {
            stripe: 'from-amber-500/80 via-amber-500/40 to-transparent',
            halo: 'bg-amber-500/15',
            icon: AlertTriangle,
            iconClass: 'text-amber-300',
            badge: 'warning' as const,
          }
        : {
            stripe: 'from-sky-500/80 via-sky-500/40 to-transparent',
            halo: 'bg-sky-500/15',
            icon: Info,
            iconClass: 'text-sky-300',
            badge: 'info' as const,
          };

  const dir = anomaly.observed >= anomaly.expected ? 'up' : 'down';
  const Icon = meta.icon;
  const delta = anomaly.observed - anomaly.expected;
  const deltaPct =
    anomaly.expected !== 0 ? (delta / Math.abs(anomaly.expected)) * 100 : null;

  return (
    <motion.li
      variants={fadeUp}
      className="relative overflow-hidden rounded-xl border border-border/60 bg-card/50 p-4 backdrop-blur"
    >
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-1 bg-gradient-to-b',
          meta.stripe,
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full blur-3xl opacity-70',
          meta.halo,
        )}
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-border/60',
                meta.halo,
              )}
            >
              <Icon className={cn('h-4 w-4', meta.iconClass)} />
            </span>
            <div>
              <div className="text-sm font-semibold text-foreground">
                {anomaly.metric}
              </div>
              <div className="text-[11px] text-muted-foreground">{anomaly.date}</div>
            </div>
          </div>
          <Badge variant={meta.badge}>{anomaly.severity}</Badge>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
          <Stat label="What happened" value={anomaly.observed.toFixed(2)} />
          <Stat label="What we'd expect" value={anomaly.expected.toFixed(2)} />
          <Stat
            label="How unusual"
            value={anomaly.z_score.toFixed(2)}
            tone={Math.abs(anomaly.z_score) >= 2 ? 'warn' : undefined}
          />
        </div>
        <div
          className={cn(
            'mt-3 inline-flex items-center gap-1 text-xs',
            dir === 'up' ? 'text-emerald-300' : 'text-rose-300',
          )}
        >
          {dir === 'up' ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          {deltaPct !== null ? `${deltaPct >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%` : '—'}{' '}
          vs expected
        </div>
      </div>
    </motion.li>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warn';
}): JSX.Element {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 p-2">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 font-mono text-sm',
          tone === 'warn' ? 'text-amber-300' : 'text-foreground',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function RecommendationsSection({
  recommendations,
}: {
  recommendations: Recommendation[] | null;
}): JSX.Element {
  const sorted = useMemo(
    () => (recommendations ? [...recommendations].sort((a, b) => b.score - a.score) : []),
    [recommendations],
  );

  return (
    <motion.section variants={fadeUp} className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-foreground">What to do next</h2>
        <p className="text-sm text-muted-foreground">
          Actions ranked by how much they can move the needle vs how hard they are to do.
        </p>
      </div>
      {sorted.length === 0 ? (
        <EmptyState
          title="Writing your recommendations..."
          description="Your list of next steps will appear here shortly."
        />
      ) : (
        <motion.ol
          variants={stagger}
          initial="hidden"
          animate="show"
          className="space-y-3"
        >
          {sorted.map((rec, i) => (
            <RecommendationItem key={`${rec.title}-${i}`} rec={rec} index={i + 1} />
          ))}
        </motion.ol>
      )}
    </motion.section>
  );
}

function RecommendationItem({
  rec,
  index,
}: {
  rec: Recommendation;
  index: number;
}): JSX.Element {
  return (
    <motion.li
      variants={fadeUp}
      className="relative overflow-hidden rounded-xl border border-border/60 bg-card/50 p-4 backdrop-blur"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/15 font-mono text-sm font-semibold text-primary ring-1 ring-primary/30">
          {String(index).padStart(2, '0')}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{rec.title}</h3>
            <Badge variant="info">score {rec.score.toFixed(1)}</Badge>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {rec.rationale}
          </p>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
            <ScoreBar label="Impact" value={rec.expected_impact} tone="primary" />
            <ScoreBar label="Easy to do" value={rec.ease} tone="success" />
          </div>
        </div>
      </div>
    </motion.li>
  );
}

function ScoreBar({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'primary' | 'success';
}): JSX.Element {
  const clamped = Math.max(0, Math.min(10, value));
  const pct = (clamped / 10) * 100;
  return (
    <div className="flex min-w-[160px] items-center gap-2">
      <span className="w-12 text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border/50">
        <div
          className={cn(
            'h-full rounded-full bg-gradient-to-r',
            tone === 'primary'
              ? 'from-primary to-violet-400'
              : 'from-emerald-400 to-sky-400',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right font-mono text-foreground">
        {value.toFixed(1)}
      </span>
    </div>
  );
}

// ─── Free-tier fallback sections ───────────────────────────────────────────
//
// Rendered below the OAuth-dependent Anomalies/Recommendations blocks. These
// sections come from `/v1/projects/:id/reports/monthly` and are always
// populated from data we already collect for free (rank_history, audits,
// keyword_runs), so users without a GSC/GA4 grant still see meaningful signal.

function FreeTierSectionsBlock({
  view,
}: {
  view: MonthlyReportView;
}): JSX.Element {
  const { rank_movement, technical_health, discovery } = view.sections;
  return (
    <motion.section variants={fadeUp} className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold text-foreground">
          Movement this month
        </h2>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
          {view.mode}
        </Badge>
      </div>
      {view.connect_prompts.length > 0 ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
          {view.connect_prompts.map((p) => (
            <div key={p.connect_url} className="flex items-center gap-2">
              <Info className="h-3.5 w-3.5 shrink-0" />
              <span>{p.message}</span>
              <ConnectPromptLink url={p.connect_url} />
            </div>
          ))}
        </div>
      ) : null}
      <div className="grid gap-4 md:grid-cols-3">
        <RankMovementCard section={rank_movement} />
        <TechnicalHealthCard section={technical_health} />
        <DiscoveryCard section={discovery} />
      </div>
    </motion.section>
  );
}

function SectionShell({
  title,
  state,
  emptyReason,
  children,
}: {
  title: string;
  state: 'populated' | 'empty' | 'na';
  emptyReason: string | null;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <GlassSurface className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <Badge
          variant="outline"
          className={cn(
            'text-[10px] uppercase',
            state === 'populated'
              ? 'border-emerald-500/40 text-emerald-300'
              : state === 'empty'
                ? 'border-muted text-muted-foreground'
                : 'border-amber-500/40 text-amber-300',
          )}
        >
          {state}
        </Badge>
      </div>
      {state === 'populated' ? (
        children
      ) : (
        <p className="text-xs text-muted-foreground">
          {emptyReason ?? 'No data for this period.'}
        </p>
      )}
    </GlassSurface>
  );
}

function RankMovementCard({
  section,
}: {
  section: RankMovementSection;
}): JSX.Element {
  return (
    <SectionShell
      title="Rank movement"
      state={section.state}
      emptyReason={section.empty_reason}
    >
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Tracked keywords</span>
          <span className="font-mono text-foreground">
            {section.tracked_keyword_count}
          </span>
        </div>
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Avg Δ position</span>
          <span
            className={cn(
              'font-mono',
              section.avg_position_delta !== null &&
                section.avg_position_delta > 0
                ? 'text-emerald-300'
                : section.avg_position_delta !== null &&
                    section.avg_position_delta < 0
                  ? 'text-rose-300'
                  : 'text-foreground',
            )}
          >
            {section.avg_position_delta !== null
              ? section.avg_position_delta.toFixed(1)
              : '—'}
          </span>
        </div>
        {section.gainers.length > 0 ? (
          <div className="space-y-1 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Top gainers
            </div>
            {section.gainers.slice(0, 3).map((g) => (
              <div
                key={g.tracked_keyword_id}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate">{g.keyword}</span>
                <span className="shrink-0 font-mono text-emerald-300">
                  +{g.delta}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {section.losers.length > 0 ? (
          <div className="space-y-1 pt-1">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Top losers
            </div>
            {section.losers.slice(0, 3).map((l) => (
              <div
                key={l.tracked_keyword_id}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate">{l.keyword}</span>
                <span className="shrink-0 font-mono text-rose-300">
                  {l.delta}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}

function TechnicalHealthCard({
  section,
}: {
  section: TechnicalHealthSection;
}): JSX.Element {
  return (
    <SectionShell
      title="Technical health"
      state={section.state}
      emptyReason={section.empty_reason}
    >
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Latest health score</span>
          <span className="font-mono text-foreground">
            {section.latest_health_score ?? '—'}
          </span>
        </div>
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Criticals opened</span>
          <span className="font-mono text-rose-300">
            +{section.criticals_opened}
          </span>
        </div>
        <div className="flex items-center justify-between text-muted-foreground">
          <span>Criticals closed</span>
          <span className="font-mono text-emerald-300">
            −{section.criticals_closed}
          </span>
        </div>
        {section.recurring_issues.length > 0 ? (
          <div className="space-y-1 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Recurring issues
            </div>
            {section.recurring_issues.slice(0, 3).map((r) => (
              <div
                key={r.check_id}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate">{r.check_id}</span>
                <span className="shrink-0 font-mono text-muted-foreground">
                  {r.count}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}

function DiscoveryCard({
  section,
}: {
  section: DiscoverySection;
}): JSX.Element {
  return (
    <SectionShell
      title="Discovery"
      state={section.state}
      emptyReason={section.empty_reason}
    >
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between text-muted-foreground">
          <span>New keywords surfaced</span>
          <span className="font-mono text-foreground">
            {section.new_keyword_count}
          </span>
        </div>
        {Object.keys(section.intent_breakdown).length > 0 ? (
          <div className="space-y-1 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              By intent
            </div>
            {Object.entries(section.intent_breakdown).map(([intent, count]) => (
              <div
                key={intent}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate capitalize">{intent}</span>
                <span className="shrink-0 font-mono">{count}</span>
              </div>
            ))}
          </div>
        ) : null}
        {section.top_clusters.length > 0 ? (
          <div className="space-y-1 pt-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Top clusters
            </div>
            {section.top_clusters.slice(0, 3).map((c) => (
              <div
                key={c.cluster_id}
                className="flex items-center justify-between gap-2 text-foreground"
              >
                <span className="truncate">{c.label}</span>
                <span className="shrink-0 font-mono text-muted-foreground">
                  {c.size}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </SectionShell>
  );
}

/**
 * `connect_url` comes from the API payload. Internal paths get an SPA
 * navigation (no full reload); anything else opens hardened in a new tab —
 * never a raw same-tab anchor to an unvalidated URL.
 */
function ConnectPromptLink({ url }: { url: string }): JSX.Element {
  const className =
    'ml-auto underline decoration-dotted underline-offset-2 hover:text-amber-100';
  const isInternal = url.startsWith('/') && !url.startsWith('//');
  if (isInternal) {
    return (
      <Link to={url as string} className={className}>
        Connect
      </Link>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
      Connect
    </a>
  );
}
